import { useCallback, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { useAtom } from 'jotai'
import {
  frameRefAtom,
  fullscreenAtom,
  videoElementAtom,
  wControlsVisibilityAtom,
  wVisibilityTimerIdAtom,
} from './atoms'
import { FullScreenMaximize } from '@styled-icons/fluentui-system-regular/FullScreenMaximize'
import { FullScreenMinimize } from '@styled-icons/fluentui-system-regular/FullScreenMinimize'
import useAtomWithRef from '../../../utils/useAtomWithRef'

/*======================== React component ===================================*/

const FullscreenToggle = (): JSX.Element => {
  const [isFullscreen, setFullscreen] = useAtom(fullscreenAtom)
  const [videoElement] = useAtom(videoElementAtom)
  const [frameRef] = useAtomWithRef(frameRefAtom)

  const [, setVisibilityTimerId] = useAtom(wVisibilityTimerIdAtom)
  const [, setControlsVisibility] = useAtom(wControlsVisibilityAtom)

  const isIPhone = useMemo(() => {
    return navigator.userAgent.match(/iPhone/i)
  }, [])

  // Declare document as any type. Otherwise we get errors when checking for
  // fullscreen support for Safari.
  const document: any = window.document

  const handleFullscreenChange = useCallback(() => {
    setFullscreen(checkIfFullscreen())
  }, [setFullscreen])

  // Detect fullscreen changes and update state.
  useEffect(() => {
    // Set up the listeneres for all possible platforms.
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
    document.addEventListener('mozfullscreenchange', handleFullscreenChange)
    document.addEventListener('msfullscreenchange', handleFullscreenChange)

    return (): void => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange)
    }
  }, [])

  return (
    <Wrapper>
      {isFullscreen ? (
        <FullScreenMinimize
          onClick={() => {
            cancelFullscreen()

            // As the logic here applies to web and mobile, we just set the
            // controls visibility to false and cancel the timer.
            setVisibilityTimerId('cancel')
            setControlsVisibility(false)
          }}
        />
      ) : (
        <FullScreenMaximize
          onClick={() => {
            // In some cases, we want to make the video element fullscreen, but in other
            // cases, we want to make the frame fullscreen.
            // This is a trick which allows us to show the custom overlay instead of the
            // native controls.
            isIPhone ? makeFullScreen(videoElement) : makeFullScreen(frameRef.current)
            setVisibilityTimerId('restart')
          }}
        />
      )}
    </Wrapper>
  )
}

export default FullscreenToggle

/*======================== Styled components =================================*/

const Wrapper = styled.div`
  display: inline-block;
  cursor: pointer;
  pointer-events: auto;
  color: white;
  height: 100%;
  width: 100%;
`

/*============================= Functions =====================================*/

/**
 *  Requests the given element to be shown in fullscreen element.
 *
 * @param element Element to set to fullscreen.
 */
function makeFullScreen(element: any): void {
  if (element.requestFullscreen) {
    element.requestFullscreen()
  } else if (element.msRequestFullscreen) {
    element.msRequestFullscreen()
  } else if (element.mozRequestFullScreen) {
    element.mozRequestFullScreen()
  } else if (element.webkitRequestFullscreen) {
    element.webkitRequestFullscreen()
  } else if (element.webkitEnterFullScreen) {
    element.webkitEnterFullScreen()
  } else if (element.webkitRequestFullScreen) {
    element.webkitRequestFullScreen()
  } else {
    console.log('Fullscreen API is not supported')
  }
}

function checkIfFullscreen(): boolean {
  const document: any = window.document

  if (
    document.fullscreenElement ||
    document.msFullscreenElement ||
    document.mozFullScreenElement ||
    document.webkitFullscreenElement
  ) {
    return true
  }

  return false
}

function cancelFullscreen(): void {
  const document: any = window.document
  if (document.exitFullscreen) {
    document.exitFullscreen()
  } else if (document.msExitFullscreen) {
    document.msExitFullscreen()
  } else if (document.mozCancelFullScreen) {
    document.mozCancelFullScreen()
  } else if (document.webkitCancelFullScreen) {
    document.webkitCancelFullScreen()
  }
}
